/**
 * Iter 44 — Admin → Provider message composer (MVP).
 *
 * FP3-069 follow-up #14 — Multi-recipient + channel-aware send.
 *
 * The composer now accepts a list of recipient candidates (instead of
 * a single hard-wired provider). Default selection is the first
 * candidate (the supervision-aware default the backend computed). The
 * channel field switches the underlying API call:
 *
 *   • `admin_note`   → POST /api/messages (legacy admin → provider
 *                       contextual note channel).
 *   • `provider_dm` → POST /api/provider-messages (FP3-029B6A
 *                       provider↔provider DM channel). Used for
 *                       supervisor → assistant + assistant →
 *                       supervisor messages so the recipient sees
 *                       them in the merged Messages inbox shipped in
 *                       FP3-069 follow-up #13.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, UserCircle, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_LABELS = {
  progress_goal: "Goal progress",
  progress_student: "Student progress",
  coverage_violation: "Coverage / minutes",
  compliance: "Compliance",
  scheduling: "Scheduling",
  service: "Service delivery",
  general: "General",
};

function suggestSubject(context) {
  if (!context) return "";
  const who = context.student_name ? context.student_name : "";
  if (context.kind === "progress_goal" && context.goal_label) {
    return `${who ? who + " · " : ""}goal: ${context.goal_label}`;
  }
  if (context.kind === "progress_student" && who) {
    return `Progress check-in · ${who}`;
  }
  if (context.kind === "coverage_violation" && context.violation_label) {
    return `${context.violation_label}${who ? " · " + who : ""}`;
  }
  if (context.kind === "compliance" && who) {
    return `Compliance review · ${who}`;
  }
  if (context.kind === "scheduling") {
    return `Scheduling${who ? " · " + who : ""}`;
  }
  return who ? `Re: ${who}` : "";
}

export function MessageComposerDialog({
  open,
  onOpenChange,
  // Legacy single-recipient API kept for back-compat with surfaces
  // that don't have the supervision-aware candidate list (AssistantsCard
  // already passes `provider`).
  provider,
  // FP3-069 follow-up #14 — Supervision-aware recipient list. When
  // present, takes precedence over `provider`. Each candidate must
  // carry `provider_id`, `name`, optional `role`, `is_assistant`,
  // optional `user_id` (the assistant's user_id — REQUIRED when the
  // channel is `provider_dm` because the DM endpoint addresses by
  // user_id, not provider_id).
  candidates,
  // FP3-069 follow-up #14 — Channel = "admin_note" (default) or
  // "provider_dm".
  channel = "admin_note",
  context,
  onSent,
}) {
  const list = useMemo(() => {
    if (Array.isArray(candidates) && candidates.length) return candidates;
    if (provider?.id)
      return [
        {
          provider_id: provider.id,
          name: provider.name || "Provider",
          role: provider.role || "",
          is_assistant: false,
        },
      ];
    return [];
  }, [candidates, provider]);

  const [recipientPid, setRecipientPid] = useState(list[0]?.provider_id || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(suggestSubject(context));
      setBody("");
      setRecipientPid(list[0]?.provider_id || "");
    }
  }, [open, context, list]);

  const recipient = list.find((c) => c.provider_id === recipientPid) || list[0];
  const needsSubject = channel === "admin_note";
  const disabled =
    (needsSubject && !subject.trim())
    || !body.trim()
    || !recipient?.provider_id
    || sending;

  const handleSend = async () => {
    if (disabled) return;
    setSending(true);
    try {
      let resp;
      if (channel === "provider_dm") {
        // FP3-069 follow-up #13/#14 — DM channel. Recipient must
        // carry `user_id` because /provider-messages addresses by
        // user_id. The recipient-candidates endpoint returns it on
        // each candidate; fall back to a friendly error if a caller
        // hand-rolled a candidate without it.
        if (!recipient.user_id) {
          throw new Error("Recipient is missing user_id for DM channel");
        }
        const ctxLabelParts = [];
        if (context?.student_name) ctxLabelParts.push(context.student_name);
        if (context?.goal_label) ctxLabelParts.push(`goal: ${context.goal_label}`);
        else if (context?.violation_label) ctxLabelParts.push(context.violation_label);
        else if (context?.ref_label) ctxLabelParts.push(context.ref_label);
        resp = await api.sendProviderMessage({
          to_user_id: recipient.user_id,
          body: body.trim(),
          context_type: context?.kind || "general",
          context_id: context?.goal_id || context?.student_id || "",
          context_label: ctxLabelParts.join(" · "),
        });
      } else {
        resp = await api.createMessage({
          to_provider_id: recipient.provider_id,
          subject: subject.trim(),
          body: body.trim(),
          context: context || null,
        });
      }
      toast.success(`Message sent to ${recipient.name || "provider"}`);
      if (typeof onSent === "function") onSent(resp.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const kindLabel = context?.kind ? KIND_LABELS[context.kind] || context.kind : null;
  const showPicker = list.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="message-composer-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1F2937] heading-font flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="duotone" className="text-[#3F4756]" />
            {channel === "provider_dm" ? "Message provider" : "Message provider"}
          </DialogTitle>
          <DialogDescription className="text-[#6B7280]">
            {channel === "provider_dm"
              ? "Direct provider-to-provider message. The recipient will see it in their Messages inbox."
              : "In-app note. The provider will see it in their Messages inbox."}
          </DialogDescription>
        </DialogHeader>

        {/* Recipient */}
        {showPicker ? (
          <div
            className="flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-[#F3F1EC] border border-[#E5E3DB]"
            data-testid="message-recipient-picker"
          >
            <div className="flex items-center gap-2">
              <UserCircle size={16} weight="duotone" className="text-[#3F4756]" />
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">To</span>
            </div>
            <select
              data-testid="message-recipient-select"
              value={recipientPid}
              onChange={(e) => setRecipientPid(e.target.value)}
              className="text-[13px] bg-white border border-[#E5E3DB] rounded px-2 py-1.5"
            >
              {list.map((c) => (
                <option key={c.provider_id} value={c.provider_id}>
                  {c.name}
                  {c.role ? ` · ${c.role}` : ""}
                  {c.is_assistant ? " · assistant" : ""}
                  {c.is_supervisor ? " · supervisor" : ""}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F3F1EC] border border-[#E5E3DB]"
            data-testid="message-recipient"
          >
            <UserCircle size={16} weight="duotone" className="text-[#3F4756]" />
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">To</span>
            <span
              data-testid="message-recipient-name"
              className="text-[13px] font-medium text-[#1F2937]"
            >
              {recipient?.name || "—"}
            </span>
            {recipient?.role && (
              <span className="text-[11px] text-[#6B7280]">· {recipient.role}</span>
            )}
            {recipient?.is_assistant && (
              <span className="ml-1 inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-[1px] rounded-full bg-[#EEF1F6] text-[#3F4756]">
                Assistant
              </span>
            )}
          </div>
        )}

        {/* Context chips */}
        {context && (
          <div
            className="rounded-lg border border-[#E8E4D8] bg-white p-3 space-y-1.5"
            data-testid="message-context-summary"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
              <Tag size={11} weight="duotone" /> Context
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {kindLabel && (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#3F4756]/10 text-[#3F4756]">
                  {kindLabel}
                </span>
              )}
              {context.student_name && (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#1E3F33]/10 text-[#1E3F33]">
                  {context.student_name}
                  {context.student_readable_id && (
                    <span className="ml-1 opacity-70 font-mono text-[10px]">
                      · {context.student_readable_id}
                    </span>
                  )}
                </span>
              )}
              {context.goal_label && (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#4A6B8C]/10 text-[#4A6B8C]">
                  Goal: {context.goal_label}
                </span>
              )}
              {context.violation_label && (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#9C6B2E]/10 text-[#9C6B2E]">
                  {context.violation_label}
                </span>
              )}
              {context.ref_label && !context.violation_label && (
                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#9B9890]/15 text-[#6B7280]">
                  {context.ref_label}
                </span>
              )}
            </div>
          </div>
        )}

        {needsSubject && (
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Subject</label>
            <Input
              data-testid="message-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short subject line"
              maxLength={200}
              className="h-9 text-sm"
            />
          </div>
        )}

        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Message</label>
          <Textarea
            data-testid="message-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What would you like the recipient to know?"
            rows={5}
            maxLength={4000}
            className="text-sm resize-none"
          />
          <div className="text-[10px] text-[#9B9890] text-right">{body.length}/4000</div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="message-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={disabled}
            data-testid="message-send"
            className="bg-[#1E3F33] hover:bg-[#18322A] text-white"
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
